import { useEffect, useLayoutEffect, useRef } from "react";

export const FORM_AUTO_SAVE_INTERVAL_MS = 2 * 60 * 1000;

// onSave must use the background-save path, forward signal to its requests,
// and check isCurrent() before applying a response to the editor.
// The save handler owns manual-save coordination and saved-snapshot tracking.
export const useFormAutoSave = ({
  enabled = false,
  documentKey,
  hasUnsavedChanges = false,
  isSaving = false,
  onSave,
}) => {
  const latestRef = useRef(null);
  const inFlightRef = useRef(null);

  // Keep the committed callback and guards current without restarting the
  // two-minute timer on every keystroke or editor render.
  useLayoutEffect(() => {
    latestRef.current = {
      enabled,
      documentKey,
      hasUnsavedChanges,
      isSaving,
      onSave,
    };
  }, [enabled, documentKey, hasUnsavedChanges, isSaving, onSave]);

  useEffect(() => {
    if (!enabled || !documentKey) return undefined;

    let active = true;
    let activeController = null;

    const isCurrent = () =>
      active &&
      latestRef.current?.enabled &&
      latestRef.current?.documentKey === documentKey;

    const autoSave = async () => {
      const latest = latestRef.current;

      if (
        !isCurrent() ||
        !latest.hasUnsavedChanges ||
        latest.isSaving ||
        inFlightRef.current ||
        typeof latest.onSave !== "function"
      ) {
        return;
      }

      const controller = new AbortController();
      activeController = controller;
      inFlightRef.current = controller;

      try {
        await latest.onSave({
          signal: controller.signal,
          isCurrent: () => isCurrent() && !controller.signal.aborted,
        });
      } catch {
        // Background failures must not interrupt editing. The save handler
        // leaves failed changes pending so a later interval can retry them.
      } finally {
        if (inFlightRef.current === controller) {
          inFlightRef.current = null;
        }
        if (activeController === controller) {
          activeController = null;
        }
      }
    };

    const interval = window.setInterval(autoSave, FORM_AUTO_SAVE_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(interval);
      activeController?.abort();
      // Keep the in-flight guard until the callback settles, even if the
      // editor is immediately reopened or switches to another document.
    };
  }, [enabled, documentKey]);
};